import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { startOfDay, subDays, subMonths, subYears } from 'date-fns';

import {
  fetchOutcomeStepEventsApi,
  type ListStepOutcomeEventsParams,
  type StepOutcomeEvent,
} from '@/features/system/orchestrator/api/outcomeEventsApi';
import { timeAgo } from '@/utils/date';
import { useNow } from '@/utils/useNow';

const DEFAULT_WINDOW_HOURS = 24;
const DEFAULT_LIMIT = 200;
const AI_WORKER_KEY = 'ai';

type UsageCounts = {
  total_tokens?: number;
  prompt_tokens?: number;
  completion_tokens?: number;
};

type AiInvocationRow = {
  key: string;
  created_at: string;
  workflow_key: string;
  step_key: string;
  handler_key: string;
  run_id?: number;
  status_code?: number;
  message?: string;
  finish_reason?: string;
  model?: string;
  duration_ms?: number;
  usage: UsageCounts;
  raw_events: StepOutcomeEvent[];
};

function toIso(ms: number): string {
  return new Date(ms).toISOString();
}

function toDatetimeLocalValue(iso: string): string {
  // input[type=datetime-local] expects: YYYY-MM-DDTHH:mm
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const yyyy = d.getFullYear();
  const mm = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const hh = pad(d.getHours());
  const mi = pad(d.getMinutes());
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

function fromDatetimeLocalValue(v: string): string | null {
  const s = String(v ?? '').trim();
  if (!s) return null;
  const d = new Date(s);
  const t = d.getTime();
  if (!Number.isFinite(t)) return null;
  return new Date(t).toISOString();
}

function safeText(v: unknown): string {
  return String(v ?? '').trim();
}

function tryParseJson(v: unknown): unknown {
  if (typeof v !== 'string') return v;
  const t = v.trim();
  if (!t) return v;
  if (
    (t.startsWith('{') && t.endsWith('}'))
    || (t.startsWith('[') && t.endsWith(']'))
    || (t.startsWith('"') && t.endsWith('"'))
  ) {
    try {
      return JSON.parse(t);
    } catch {
      return v;
    }
  }
  return v;
}

function asFiniteNumber(v: unknown): number | undefined {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  if (typeof v === 'string') {
    const s = v.trim();
    if (!s) return undefined;
    const n = Number(s);
    if (Number.isFinite(n)) return n;
  }
  return undefined;
}

function extractUsageCounts(v: unknown): UsageCounts {
  const parsed = tryParseJson(v);
  if (!parsed || typeof parsed !== 'object') return {};

  const anyV: any = parsed as any;
  const usage = (anyV.usage && typeof anyV.usage === 'object') ? anyV.usage : anyV;

  const total_tokens = asFiniteNumber(usage.total_tokens ?? usage.totalTokens ?? usage.tokens ?? usage.token_count ?? usage.tokenCount);
  const prompt_tokens = asFiniteNumber(usage.prompt_tokens ?? usage.promptTokens);
  const completion_tokens = asFiniteNumber(usage.completion_tokens ?? usage.completionTokens);

  const out: UsageCounts = {};
  if (total_tokens != null) out.total_tokens = total_tokens;
  if (prompt_tokens != null) out.prompt_tokens = prompt_tokens;
  if (completion_tokens != null) out.completion_tokens = completion_tokens;
  return out;
}

function pickFirstString(...values: unknown[]): string | undefined {
  for (const v of values) {
    const s = typeof v === 'string' ? v.trim() : '';
    if (s) return s;
  }
  return undefined;
}

function pickFirstNumber(...values: unknown[]): number | undefined {
  for (const v of values) {
    const n = asFiniteNumber(v);
    if (n != null) return n;
  }
  return undefined;
}

function sumUsage(a: UsageCounts, b: UsageCounts): UsageCounts {
  const total_tokens = (a.total_tokens ?? 0) + (b.total_tokens ?? 0);
  const prompt_tokens = (a.prompt_tokens ?? 0) + (b.prompt_tokens ?? 0);
  const completion_tokens = (a.completion_tokens ?? 0) + (b.completion_tokens ?? 0);
  const out: UsageCounts = {};
  if (total_tokens) out.total_tokens = total_tokens;
  if (prompt_tokens) out.prompt_tokens = prompt_tokens;
  if (completion_tokens) out.completion_tokens = completion_tokens;
  return out;
}

export default function WorkflowAiConsumePanel() {
  const nowDate = useNow(5_000);
  const nowMs = nowDate.getTime();
  const defaultFromIso = toIso(nowMs - DEFAULT_WINDOW_HOURS * 60 * 60 * 1000);
  const defaultToIso = toIso(nowMs);

  const [fromLocal, setFromLocal] = useState<string>(toDatetimeLocalValue(defaultFromIso));
  const [toLocal, setToLocal] = useState<string>(toDatetimeLocalValue(defaultToIso));

  const [rangePreset, setRangePreset] = useState<'today' | 'last_week' | 'last_month' | 'last_6_months' | 'last_year' | 'custom'>('custom');

  const applyPreset = (preset: typeof rangePreset) => {
    setRangePreset(preset);
    if (preset === 'custom') return;

    const now = new Date();
    const to = now;
    const from = (() => {
      switch (preset) {
        case 'today':
          return startOfDay(now);
        case 'last_week':
          return subDays(now, 7);
        case 'last_month':
          return subMonths(now, 1);
        case 'last_6_months':
          return subMonths(now, 6);
        case 'last_year':
          return subYears(now, 1);
        default:
          return startOfDay(now);
      }
    })();

    setFromLocal(toDatetimeLocalValue(from.toISOString()));
    setToLocal(toDatetimeLocalValue(to.toISOString()));
  };

  const [selected, setSelected] = useState<AiInvocationRow | null>(null);

  const params: ListStepOutcomeEventsParams = useMemo(() => {
    const from = fromDatetimeLocalValue(fromLocal);
    const to = fromDatetimeLocalValue(toLocal);

    return {
      include_history: true,
      from: from ?? undefined,
      to: to ?? undefined,
      limit: DEFAULT_LIMIT,
      worker_key: AI_WORKER_KEY,
    };
  }, [fromLocal, toLocal]);

  const query = useQuery({
    queryKey: ['developer', 'workflowRunner', 'aiConsume', 'stepOutcomeEvents', params] as const,
    queryFn: async () => {
      if (!params.from || !params.to) return { items: [] as StepOutcomeEvent[] } as any;
      return await fetchOutcomeStepEventsApi(params);
    },
    staleTime: 5_000,
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const items: StepOutcomeEvent[] = Array.isArray((query.data as any)?.items) ? ((query.data as any).items as StepOutcomeEvent[]) : [];

  const invocations: AiInvocationRow[] = useMemo(() => {
    const map = new Map<string, {
      created_at: string;
      workflow_key: string;
      step_key: string;
      handler_key: string;
      run_id?: number;
      fields: Record<string, unknown>;
      raw_events: StepOutcomeEvent[];
    }>();

    for (const e of items) {
      const runId = typeof (e as any).run_id === 'number' ? (e as any).run_id as number : undefined;
      const workflow = safeText((e as any).workflow_key);
      const step = safeText((e as any).step_key);
      const handler = safeText((e as any).handler_key);
      const created = safeText((e as any).created_at);

      const groupKey = `${runId ?? '—'}:${workflow || '—'}:${step || '—'}:${handler || '—'}`;
      const prev = map.get(groupKey);

      const outcomeKey = safeText((e as any).outcome_key);
      const val = (e as any).value;

      if (!prev) {
        map.set(groupKey, {
          created_at: created,
          workflow_key: workflow,
          step_key: step,
          handler_key: handler,
          run_id: runId,
          fields: outcomeKey ? { [outcomeKey]: val } : {},
          raw_events: [e],
        });
        continue;
      }

      // Keep the latest created_at within the group (best-effort).
      const prevTime = Date.parse(prev.created_at);
      const curTime = Date.parse(created);
      if (Number.isFinite(curTime) && (!Number.isFinite(prevTime) || curTime > prevTime)) {
        prev.created_at = created;
      }

      if (outcomeKey) prev.fields[outcomeKey] = val;
      prev.raw_events.push(e);
    }

    const rows: AiInvocationRow[] = [];

    map.forEach((g, key) => {
      const f = g.fields;

      const outputsResult = tryParseJson(f['outputs.result']);
      const outputsUsage = extractUsageCounts(f['outputs.usage']);
      const outputsAnalyticsUsage = extractUsageCounts(f['outputs.analytics.usage']);
      const outputsResultUsage = extractUsageCounts((outputsResult as any)?.usage);
      const usage = sumUsage(sumUsage(outputsUsage, outputsAnalyticsUsage), outputsResultUsage);

      const model = pickFirstString(
        f['outputs.model'],
        (outputsResult as any)?.model,
        f['model'],
      );

      const finish_reason = pickFirstString(
        f['outputs.finish_reason'],
        (outputsResult as any)?.finish_reason,
        f['finish_reason'],
      );

      const status_code = pickFirstNumber(
        f['status_code'],
        f['outputs.status_code'],
        (outputsResult as any)?.status_code,
      );

      const duration_ms = pickFirstNumber(
        f['duration_ms'],
        f['outputs.duration_ms'],
        (outputsResult as any)?.duration_ms,
      );

      const message = pickFirstString(
        f['message'],
        f['outputs.message'],
        (outputsResult as any)?.message,
      );

      rows.push({
        key,
        created_at: g.created_at,
        workflow_key: g.workflow_key,
        step_key: g.step_key,
        handler_key: g.handler_key,
        run_id: g.run_id,
        status_code,
        message,
        finish_reason,
        model,
        duration_ms,
        usage,
        raw_events: g.raw_events,
      });
    });

    rows.sort((a, b) => {
      const at = Date.parse(a.created_at);
      const bt = Date.parse(b.created_at);
      if (Number.isFinite(at) && Number.isFinite(bt)) return bt - at;
      return String(b.created_at).localeCompare(String(a.created_at));
    });

    return rows;
  }, [items]);

  const totals = useMemo(() => {
    let calls = 0;
    let callsWithUsage = 0;
    let tokens: UsageCounts = {};
    let durationSum = 0;
    let durationCount = 0;

    for (const r of invocations) {
      calls += 1;
      tokens = sumUsage(tokens, r.usage);
      if ((r.usage.total_tokens ?? 0) > 0 || ((r.usage.prompt_tokens ?? 0) + (r.usage.completion_tokens ?? 0)) > 0) {
        callsWithUsage += 1;
      }
      if (typeof r.duration_ms === 'number' && Number.isFinite(r.duration_ms)) {
        durationSum += r.duration_ms;
        durationCount += 1;
      }
    }

    const avgDurationMs = durationCount > 0 ? Math.round(durationSum / durationCount) : undefined;
    return { calls, callsWithUsage, tokens, avgDurationMs };
  }, [invocations]);

  return (
    <div className="h-full min-h-0 rounded-md border border-neutral-800 flex flex-col">
      <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between gap-3">
        <div className="text-sm text-neutral-200">AI CONSUME</div>
        <div className="text-xs text-neutral-500">Outcomes filtered by handler: {AI_WORKER_KEY}.*</div>
      </div>

      <div className="p-3 border-b border-neutral-800 flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">From</div>
          <input
            className="h-9 px-2 rounded-md pac-glass-input text-neutral-100 text-sm"
            type="datetime-local"
            value={fromLocal}
            onChange={(e) => {
              setRangePreset('custom');
              setFromLocal(e.target.value);
            }}
          />
        </div>

        <div className="flex flex-col gap-1">
          <div className="text-xs text-neutral-500">To</div>
          <input
            className="h-9 px-2 rounded-md pac-glass-input text-neutral-100 text-sm"
            type="datetime-local"
            value={toLocal}
            onChange={(e) => {
              setRangePreset('custom');
              setToLocal(e.target.value);
            }}
          />
        </div>

        <div className="flex-1" />

        <button
          className="h-9 px-3 rounded-md bg-transparent border border-neutral-700 text-neutral-100 text-sm hover:border-neutral-500"
          onClick={() => query.refetch()}
        >
          Refresh
        </button>

        <div className="w-full order-last flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-neutral-400">
          <button
            type="button"
            onClick={() => applyPreset('today')}
            className={(rangePreset === 'today' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Today
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_week')}
            className={(rangePreset === 'last_week' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last week
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_month')}
            className={(rangePreset === 'last_month' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last month
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_6_months')}
            className={(rangePreset === 'last_6_months' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last 6 months
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('last_year')}
            className={(rangePreset === 'last_year' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Last year
          </button>
          <span className="text-neutral-700">·</span>
          <button
            type="button"
            onClick={() => applyPreset('custom')}
            className={(rangePreset === 'custom' ? 'text-neutral-100 font-medium' : 'text-neutral-400') + ' hover:text-neutral-100'}
          >
            Custom
          </button>
        </div>
      </div>

      <div className="p-3 flex-1 min-h-0 overflow-auto space-y-3">
        {!params.from || !params.to ? (
          <div className="text-sm text-amber-300">Set valid From/To to query outcomes.</div>
        ) : query.isLoading ? (
          <div className="text-sm text-neutral-400">Loading outcomes…</div>
        ) : query.error ? (
          <div className="text-sm text-rose-400">Failed to load outcomes: {(query.error as any)?.message ?? String(query.error)}</div>
        ) : (
          <>
            <div className="text-xs text-neutral-500">
              Showing up to {DEFAULT_LIMIT} rows. Window: {params.from} → {params.to}
            </div>

            {invocations.length === 0 ? (
              <div className="text-sm text-neutral-600">No AI outcomes found for the selected window.</div>
            ) : (
              <div className="rounded-md border border-neutral-800 overflow-hidden">
                <div className="px-3 py-2 border-b border-neutral-800 flex flex-wrap items-center gap-x-6 gap-y-1 text-xs text-neutral-300">
                  <div>
                    <span className="text-neutral-500">Calls:</span> {totals.calls}
                  </div>
                  <div>
                    <span className="text-neutral-500">Calls w/ usage:</span> {totals.callsWithUsage}
                  </div>
                  <div>
                    <span className="text-neutral-500">Total tokens:</span> {(totals.tokens.total_tokens ?? ((totals.tokens.prompt_tokens ?? 0) + (totals.tokens.completion_tokens ?? 0))) || 0}
                  </div>
                  <div>
                    <span className="text-neutral-500">Prompt:</span> {totals.tokens.prompt_tokens ?? 0}
                  </div>
                  <div>
                    <span className="text-neutral-500">Completion:</span> {totals.tokens.completion_tokens ?? 0}
                  </div>
                  <div>
                    <span className="text-neutral-500">Avg duration:</span> {totals.avgDurationMs != null ? `${totals.avgDurationMs}ms` : '—'}
                  </div>
                </div>
                <table className="w-full text-left text-xs">
                  <thead className="border-b border-neutral-800">
                    <tr>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Created</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Ago</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Workflow</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Step</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Handler</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Status</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Model</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Tokens</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Duration</th>
                      <th className="px-2 py-1 text-neutral-500 font-medium">Finish</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-neutral-800">
                    {invocations.map((r) => {
                      const created = safeText(r.created_at);
                      const ago = timeAgo(created);
                      const workflow = safeText(r.workflow_key) || '—';
                      const step = safeText(r.step_key) || '—';
                      const handler = safeText(r.handler_key) || '—';

                      const statusLabel = r.status_code != null
                        ? `${r.status_code}${r.message ? ` ${r.message}` : ''}`
                        : (r.message ?? '—');

                      const totalTokens = r.usage.total_tokens
                        ?? ((r.usage.prompt_tokens ?? 0) + (r.usage.completion_tokens ?? 0));
                      const tokensLabel = totalTokens
                        ? `${totalTokens} (p${r.usage.prompt_tokens ?? 0} / c${r.usage.completion_tokens ?? 0})`
                        : '—';

                      const durationLabel = typeof r.duration_ms === 'number' && Number.isFinite(r.duration_ms)
                        ? `${Math.round(r.duration_ms)}ms`
                        : '—';

                      const isSelected = selected?.key === r.key;

                      return (
                        <tr
                          key={r.key}
                          className={(isSelected ? 'bg-white/[0.04] ' : '') + 'hover:bg-white/[0.02] align-top cursor-pointer'}
                          onClick={() => setSelected(r)}
                        >
                          <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap">{created}</td>
                          <td className="px-2 py-1 text-neutral-300 whitespace-nowrap" title={created}>{ago}</td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[16rem]"><div className="truncate" title={workflow}>{workflow}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[16rem]"><div className="truncate" title={step}>{step}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[16rem]"><div className="truncate" title={handler}>{handler}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={statusLabel}>{statusLabel}</div></td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[14rem]"><div className="truncate" title={r.model ?? ''}>{r.model ?? '—'}</div></td>
                          <td className="px-2 py-1 text-neutral-300 font-mono max-w-[14rem]"><div className="truncate" title={tokensLabel}>{tokensLabel}</div></td>
                          <td className="px-2 py-1 text-neutral-300 font-mono whitespace-nowrap" title={durationLabel}>{durationLabel}</td>
                          <td className="px-2 py-1 text-neutral-200 max-w-[10rem]"><div className="truncate" title={r.finish_reason ?? ''}>{r.finish_reason ?? '—'}</div></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="rounded-md border border-neutral-800 overflow-hidden">
              <div className="px-3 py-2 border-b border-neutral-800 flex items-center justify-between">
                <div className="text-sm text-neutral-200">Selected JSON</div>
                <div className="text-xs text-neutral-500">Click a row to inspect full event payload</div>
              </div>
              <pre className="p-3 text-xs text-neutral-200 font-mono overflow-auto max-h-[18rem] whitespace-pre-wrap break-words">
                {selected ? JSON.stringify(selected.raw_events, null, 2) : '—'}
              </pre>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
